import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {ActivatedRoute, Params, Router, UrlSegment} from '@angular/router';
import {Subscription} from 'rxjs';
import {DefaultPage} from 'src/app/app-common/const';
import {ToolsService} from '../../services/tools.service';
import {QueryParams} from '../../types/QueryParams';
import {UrlTermsSymbolsEnum} from '../../enums/SeoEnums';
import {PagerPage} from '../../types/PagerPage';
import {UrlBuilderService} from '../../services/url-builder.service';
import {PagerV2} from '../../types/Pager-v2';
import {RoutesEnums} from '../../enums/RoutesEnums';

@Component({
  selector: 'app-pagination-v2',
  templateUrl: './pagination-v2.component.html',
  styleUrls: ['./pagination-v2.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginationV2Component implements OnInit, OnChanges, OnDestroy {
  @Input() total: number = 0;
  @Input() currentPage: number;
  @Input() pageSize: number;
  @Input() maxLength: number;
  @Input() displayLabels: boolean = false;
  @Input() pageParameterName: string;
  @Input() baseUrlTab: string[];
  @Input() overwriteRouteParams: QueryParams;
  @Input() getRouterLink: (page: number) => string[];
  @Input() getQueryParams: (page: number, pageParamName: string) => QueryParams;
  @Output() pageChanged = new EventEmitter<number>();

  public baseRouterLink: string[] = [];
  public routerLink: string[] = [];
  public pager: PagerV2 = this.getEmptyPager();
  public visiblePages: PagerPage[] = [];
  public showFirstEllipsis: boolean = false;
  public showLastEllipsis: boolean = false;
  protected readonly Object = Object;
  private subscriptions: Subscription[] = [];
  private routerQueryParams: Params;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private tools: ToolsService,
    private urlBuilder: UrlBuilderService,
  ) {}

  public ngOnInit() {
    this.subscriptions.push(
      this.route.queryParams.subscribe(
        (params) => this.processRouteQueryParams(params),
        (error) => console.error(error),
      ),
      this.route.url.subscribe(
        (params) => this.processRouteParams(params),
        (error) => console.error(error),
      ),
    );
  }

  public ngOnChanges(): void {
    this.recalculatePager();
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  protected navigateToUrl(routerLink: string[], queryParams: QueryParams): void {
    this.router.navigate(routerLink, {queryParams: queryParams});
  }

  private processRouteQueryParams(params: Params) {
    this.routerQueryParams = params;
    this.recalculatePager();
  }

  private processRouteParams(params: UrlSegment[]) {
    if (params && params.length > 0) {
      this.routerLink = [];
      this.baseRouterLink = [];
      params.forEach((p) => this.routerLink.push(p.path));
    }
    this.recalculateBaseRouterLink();
    this.recalculatePager();
  }

  private recalculateBaseRouterLink() {
    const routerLink = this.getRouterLink(1);
    if (routerLink.length === 2 && routerLink[1] === RoutesEnums.SEARCH) {
      this.baseRouterLink = ['/'];
    } else {
      this.baseRouterLink = [...routerLink];
    }
  }

  private recalculatePager() {
    if (this.total && this.pageSize) {
      this.pager = this.getPager(this.total, this.currentPage, this.pageSize);
      this.calculateVisiblePages();
    }
  }

  private calculateVisiblePages() {
    this.visiblePages = [];
    this.showFirstEllipsis = false;
    this.showLastEllipsis = false;

    if (this.pager.totalPages <= 5) {
      this.visiblePages = this.pager.pages;
      return;
    }

    if (this.pager.totalPages === 6) {
      if (this.currentPage <= 3) {
        this.visiblePages = this.pager.pages.slice(0, 4);
        this.showLastEllipsis = true;
        this.visiblePages.push(this.pager.pages[this.pager.totalPages - 1]);
      } else {
        this.visiblePages.push(this.pager.pages[0]);
        this.showFirstEllipsis = true;
        this.visiblePages = this.visiblePages.concat(this.pager.pages.slice(2));
      }
      return;
    }

    if (this.currentPage <= 3) {
      this.visiblePages = this.pager.pages.slice(0, 4);
      this.showLastEllipsis = true;
      this.visiblePages.push(this.pager.pages[this.pager.totalPages - 1]);
    } else if (this.currentPage >= this.pager.totalPages - 2) {
      this.visiblePages.push(this.pager.pages[0]);
      this.showFirstEllipsis = true;
      this.visiblePages = this.visiblePages.concat(this.pager.pages.slice(this.pager.totalPages - 4));
    } else {
      this.visiblePages.push(this.pager.pages[0]);
      this.showFirstEllipsis = true;

      const currentIndex = this.currentPage - 1;
      this.visiblePages.push(this.pager.pages[currentIndex - 1]);
      this.visiblePages.push(this.pager.pages[currentIndex]);
      this.visiblePages.push(this.pager.pages[currentIndex + 1]);

      this.showLastEllipsis = true;
      this.visiblePages.push(this.pager.pages[this.pager.totalPages - 1]);
    }
  }

  private getPager(totalItems: number, currentPage: number, pageSize: number): PagerV2 {
    const totalPages = Math.ceil(totalItems / pageSize);
    currentPage = Math.max(1, Math.min(totalPages, currentPage));

    const startPage = 1;
    const endPage = totalPages;

    const pages: PagerPage[] = Array.from(Array(endPage).keys()).map((i) => {
      return {
        pageNumber: i + 1,
        link: {
          routerLink: this.CreateSearchResultsRoute(i + 1),
          queryParams: this.CreateSearchResultsQueryParams(i + 1),
        },
      };
    });

    return {
      totalItems,
      currentPage,
      pageSize,
      totalPages,
      startPage,
      endPage,
      pages,
      prevPage:
        currentPage === 1
          ? null
          : {
              pageNumber: this.PreviousPage(currentPage),
              routerLink: this.CreateSearchResultsRoute(this.PreviousPage(currentPage)),
              queryParams: this.CreateSearchResultsQueryParams(this.PreviousPage(currentPage)),
            },
      nextPage:
        currentPage === totalPages
          ? null
          : {
              pageNumber: this.NextPage(currentPage),
              routerLink: this.CreateSearchResultsRoute(this.NextPage(currentPage)),
              queryParams: this.CreateSearchResultsQueryParams(this.NextPage(currentPage)),
            },
    };
  }

  private getEmptyPager(): PagerV2 {
    return {
      totalItems: 0,
      currentPage: 0,
      pageSize: 0,
      totalPages: 0,
      startPage: 0,
      endPage: 0,
      pages: [],
      prevPage: null,
      nextPage: null,
    };
  }

  private CreateSearchResultsQueryParams(page: number): QueryParams {
    if (this.getQueryParams != null) {
      let queryParams = this.getQueryParams(page, this.pageParameterName);
      if (page !== DefaultPage) {
        delete queryParams[UrlTermsSymbolsEnum.FORCE_SEARCH_RESULTS];
      }
      queryParams = this.urlBuilder.cleanQueryParams(queryParams);
      queryParams = this.urlBuilder.sortQueryParams(queryParams);
      return queryParams;
    } else {
      let newRouterQueryParams: QueryParams = this.tools.clone(
        !this.tools.isNil(this.overwriteRouteParams) ? this.overwriteRouteParams : this.routerQueryParams,
      );

      if (page !== DefaultPage) {
        delete newRouterQueryParams[UrlTermsSymbolsEnum.FORCE_SEARCH_RESULTS];
        newRouterQueryParams[this.pageParameterName] = page;
      } else {
        delete newRouterQueryParams[this.pageParameterName];
      }
      newRouterQueryParams = this.urlBuilder.cleanQueryParams(newRouterQueryParams);
      newRouterQueryParams = this.urlBuilder.sortQueryParams(newRouterQueryParams);
      return newRouterQueryParams;
    }
  }

  private CreateSearchResultsRoute(page: number): string[] {
    return this.getRouterLink != null ? this.getRouterLink(page) : this.routerLink;
  }

  private NextPage(page: number): number {
    return parseInt(this.tools.clone(page).toString(), 10) + 1;
  }

  private PreviousPage(page: number): number {
    return parseInt(this.tools.clone(page).toString(), 10) - 1;
  }
}
